import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const OPTIONS = [
  { id: "hour", label: "Hour" },
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
];

/**
 * Insights time-bucket selector (Hour / Day / Week / Month). Shown next to chart type for time-series charts.
 */
export default function TimeSeriesIntervalDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const current = OPTIONS.find((o) => o.id === value) || OPTIONS[1];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={buttonRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-8 items-center gap-2 px-3 border rounded-md text-[13px] font-medium transition-colors"
        style={{
          backgroundColor: open ? "var(--surface-sunken)" : "var(--surface-base)",
          borderColor: "var(--border-default)",
          color: "var(--text-primary)",
          fontFamily: "Inter, sans-serif",
        }}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = "var(--surface-sunken)";
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = "var(--surface-base)";
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Time bucket"
      >
        <span>{current.label}</span>
        <ChevronDown
          size={16}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          style={{ color: "var(--text-secondary)" }}
        />
      </button>

      {open && (
        <div
          ref={dropdownRef}
          role="listbox"
          className="absolute left-0 top-full mt-1 z-[100] min-w-[120px] border rounded-md shadow-token-lg py-1"
          style={{
            fontFamily: "Inter, sans-serif",
            backgroundColor: "var(--surface-base)",
            borderColor: "var(--border-default)",
          }}
        >
          {OPTIONS.map((o) => {
            const isSelected = value === o.id;
            return (
              <button
                key={o.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors"
                style={{
                  backgroundColor: isSelected ? "var(--surface-hover)" : "transparent",
                  color: "var(--text-secondary)",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                    e.currentTarget.style.color = "var(--text-primary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "var(--text-secondary)";
                  }
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
